import { InformationmarkSmallCircleIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';
import { Tooltip } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { useExpectedOnetimePlanPrice, useExpectedPlanPrice, usePlanStatusType } from '../hooks';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../PlanPaymentSection/Providers';
import { PlanTemplateType } from '../types';
import { getVatPreview } from '../utils';
export const PriceInfo = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    watch
  } = usePlanPaymentFormContext();
  const {
    selectedPlanInfo,
    isUpdate,
    currencyString,
    isKrPayment,
    userCountryCode
  } = usePlanPaymentInfoContext();
  const planTemplateType = watch('planTemplateType');
  const planType = watch('planType');
  const planStatusType = usePlanStatusType({
    planType
  });
  return <div className="w-full rounded-b-[10px] bg-white px-[20px] py-[16px]" data-sentry-component="PriceInfo" data-sentry-source-file="PriceInfo.tsx">
      <div className="flex w-full items-center">
        {match([planTemplateType, isUpdate]).with([PlanTemplateType.PRODUCT_SUBSCRIPTION, true], () => match(planStatusType).with('SAME_GRADE', () => <>
                  <span className="text-body-1-sb font-semibold text-gray-900">
                    {t('subscription:SpaceDetail.menuManage.virtualCardPrice', '결제 금액')}
                  </span>
                  <span className="ml-auto text-h5 font-bold text-gray-900">
                    {`${currencyString} 0`}
                  </span>
                </>).with('DOWN_GRADE', () => <>
                  <span className="text-body-1-sb font-semibold text-gray-900">
                    {t('subscription:payment.summarySubscriptionUp.additonalPay', '추가 결제 금액')}
                  </span>
                  <span className="ml-auto text-h5 font-bold text-gray-900">
                    {`${currencyString} 0`}
                  </span>
                </>).otherwise(() => <SubscriptionPriceInfo />))
      // 일시납 현재 플랜
      .with([PlanTemplateType.PRODUCT_ONETIME, true], () => match(planStatusType).with('SAME_GRADE', () => <>
                  <span className="text-body-1-sb font-semibold text-gray-900">
                    {t('subscription:SpaceDetail.menuManage.virtualCardPrice', '결제 금액')}
                  </span>
                  <span className="ml-auto text-h5 font-bold text-gray-900">
                    {`${currencyString} 0`}
                  </span>
                </>).otherwise(() => <OnetimePriceInfo />)).otherwise(() => <>
              <span className="text-body-1-sb font-semibold text-gray-900">
                {t('subscription:SpaceDetail.menuManage.virtualCardPrice', '결제 금액')}
              </span>
              <span className="ml-auto text-h5 font-bold text-gray-900">
                {currencyString + ' '}{' '}
                {/* 신규 결제는 서버 priceInfo 우선, 구버전 응답이면 preview fallback */}
                {(selectedPlanInfo.priceInfo?.finalPrice ?? getVatPreview({
            amount: selectedPlanInfo.salePrice,
            currency: isKrPayment ? 'KRW' : 'USD',
            userCountryCode,
            scenario: planTemplateType === PlanTemplateType.PRODUCT_ONETIME ? 'PRODUCT_ONE_TIME_PAYMENT' : 'NEW_SUBSCRIPTION_FIRST_PAYMENT'
          }).finalPrice).toLocaleString()}
              </span>
            </>)}
      </div>
    </div>;
};
const SubscriptionPriceInfo = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    spaceHashId,
    selectedPlanInfo,
    isUpdate,
    planPaymentInfo,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planTemplateType = watch('planTemplateType');
  const {
    추가결제예상금액: 정기추가결제예상금액,
    미사용금액: 정기미사용금액,
    unusedDayCount: 정기unusedDayCount
  } = useExpectedPlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION,
    spaceHashId
  });
  return <>
      <div className="flex items-center gap-3xs">
        <span className="text-body-1-sb font-semibold text-gray-900">
          {t('subscription:payment.summarySubscriptionUp.additonalPay', '추가 결제 금액')}
        </span>
        <Tooltip title={t('subscription:payment.summarySubscriptionUp.additonalPayTooltipText', {
        plan: planPaymentInfo?.spacePlan?.planType.capitalize(),
        ccu: planPaymentInfo?.spacePlan?.maxCcu,
        period: planPaymentInfo?.subscription?.subscriptionPeriod,
        unusedDays: 정기unusedDayCount,
        currencyString,
        price: 정기미사용금액.toLocaleString(),
        salePrice: 정기추가결제예상금액 < 0 ? 0 : 정기추가결제예상금액.toLocaleString(),
        defaultValues: '첫 결제는 기존 결제된 {{plan}} {{ccu}}플랜의 {{period}}일 중 남은 {{unusedDays}}일에 해당하는 금액 ({{currencyString}}{{price}})이 차감되어 {{currencyString}}{{salePrice}}이 결제됩니다.'
      })} tooltipClassName="w-[288px]" arrow placement="top" data-sentry-element="Tooltip" data-sentry-source-file="PriceInfo.tsx">
          <InformationmarkSmallCircleIcon width={14} height={14} fill={Colors.Gray700} data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PriceInfo.tsx" />
        </Tooltip>
      </div>
      <span className="ml-auto text-h5 font-bold text-gray-900">
        {currencyString + ' '}
        {정기추가결제예상금액 < 0 ? 0 : 정기추가결제예상금액.toLocaleString()}
      </span>
    </>;
};
const OnetimePriceInfo = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    selectedPlanInfo,
    isUpdate,
    planPaymentInfo,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planTemplateType = watch('planTemplateType');
  const {
    추가결제예상금액: 일시납추가결제예상금액,
    unusedDayCount: 일시납unusedDayCount
  } = useExpectedOnetimePlanPrice({
    selectedPlanInfo,
    enabled: isUpdate && planTemplateType === PlanTemplateType.PRODUCT_ONETIME,
    spacePlanHashId: planPaymentInfo?.spacePlan?.spacePlanHashId ?? ''
  });
  const isN달결제 = planPaymentInfo?.spacePlan?.onetimePeriod?.includes('M');
  const 변경할플랜금액 = selectedPlanInfo.salePrice ?? 0;
  const 이전플랜금액 = planPaymentInfo?.spacePlan?.salePrice ?? 0;
  const 일시납미사용금액 = 변경할플랜금액 - 이전플랜금액;
  return <>
      <div className="flex items-center gap-3xs">
        <span className="text-body-1-sb font-semibold text-gray-900">
          {t('subscription:payment.summarySubscriptionUp.additonalPay', '추가 결제 금액')}
        </span>
        <Tooltip title={isN달결제 ? t('subscription:Invoice.priceInfo.discountInfoOntimeNmonth', {
        currencyString,
        nextPrice: 변경할플랜금액.toLocaleString(),
        prevPrice: 이전플랜금액.toLocaleString(),
        tmpPrice: 일시납미사용금액.toLocaleString(),
        unusedDays: 일시납unusedDayCount,
        salePrice: 일시납추가결제예상금액 < 0 ? 0 : 일시납추가결제예상금액.toLocaleString(),
        defaultValues: '변경할 플랜의 금액({{currencyString}}{{nextPrice}})에서 이전 플랜의 금액({{currencyString}}{{prevPrice}})이 차감된 {{currencyString}}{{tmpPrice}}에서 미사용분 {{unusedDays}}일이 일할 계산된 {{currencyString}}{{salePrice}}이 결제됩니다.'
      }) : t('subscription:Invoice.priceInfo.discountInfoOntime1day3day', {
        currencyString,
        nextPrice: 변경할플랜금액.toLocaleString(),
        prevPrice: 이전플랜금액.toLocaleString(),
        salePrice: 일시납추가결제예상금액.toLocaleString(),
        defaultValues: '변경할 플랜의 금액({{currencyString}}{{nextPrice}})에서 현재 플랜의 금액({{currencyString}}{{prevPrice}})이 차감된 {{currencyString}}{{salePrice}}이 결제됩니다.'
      })} tooltipClassName="w-[288px]" arrow placement="top" data-sentry-element="Tooltip" data-sentry-source-file="PriceInfo.tsx">
          <InformationmarkSmallCircleIcon width={14} height={14} fill={Colors.Gray700} data-sentry-element="InformationmarkSmallCircleIcon" data-sentry-source-file="PriceInfo.tsx" />
        </Tooltip>
      </div>
      <span className="ml-auto text-h5 font-bold text-gray-900">
        {currencyString + ' '}
        {일시납추가결제예상금액 < 0 ? 0 : 일시납추가결제예상금액.toLocaleString()}
      </span>
    </>;
};